import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { IAudioRecording } from '@taleemabad/db';
import { tlLogger } from './logger';
import { AudioMerge } from '../plugins/AudioMergePlugin';
import { mergeAudioBlobs } from '../hooks/useAudioRecorder';

/**
 * Builds the final upload blob for a recording.
 *
 * - Single session: returns recording.blob unchanged.
 * - Multi-session on Android: merges all blobs via AudioMergePlugin (ffmpeg-kit).
 *   Falls back to binary concat if merge fails.
 * - Multi-session on iOS/Web: uses OfflineAudioContext-based merge.
 */
export async function buildUploadBlob(recording: IAudioRecording): Promise<Blob> {
  const { priorBlobs, blob: currentBlob } = recording;

  if (!priorBlobs || priorBlobs.length === 0) {
    return currentBlob;
  }

  const allBlobs = [...priorBlobs, currentBlob].filter((b) => b && b.size > 0);

  if (allBlobs.length === 0) {
    tlLogger.error('AudioSync:BuildUploadBlob:AllBlobsEmpty', {
      recordingId: recording.id,
    });
    return currentBlob;
  }

  if (allBlobs.length === 1) {
    return allBlobs[0];
  }

  const mimeType = currentBlob.type || 'audio/webm';
  const isAndroid = Capacitor.getPlatform() === 'android';

  if (!isAndroid) {
    const totalSize = allBlobs.reduce((s, b) => s + b.size, 0);
    if (totalSize > 150 * 1024 * 1024) {
      tlLogger.error('AudioSync:BuildUploadBlob:OACSkipped:TooLarge', {
        totalSize,
      });
      return new Blob(allBlobs, { type: mimeType });
    }
    try {
      let merged = allBlobs[0];
      for (let i = 1; i < allBlobs.length; i++) {
        merged = await mergeAudioBlobs(merged, allBlobs[i], mimeType);
      }
      return merged;
    } catch (mergeError) {
      tlLogger.error(
        'AudioSync:BuildUploadBlob:OACMergeFailed:FallingBackToConcat',
        { error: mergeError },
      );
      return new Blob(allBlobs, { type: mimeType });
    }
  }

  const timestamp = Date.now();
  const inputPaths: string[] = [];
  const inputFileNames: string[] = [];

  try {
    for (let i = 0; i < allBlobs.length; i++) {
      const fileName = `tl_merge_input_${recording.id}_${timestamp}_${i}.webm`;
      inputFileNames.push(fileName);

      const blob = allBlobs[i];
      const CHUNK_SIZE = 1024 * 1024 * 2;

      for (let offset = 0; offset < blob.size; offset += CHUNK_SIZE) {
        const slice = blob.slice(offset, offset + CHUNK_SIZE);

        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () =>
            resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(slice);
        });

        if (offset === 0) {
          await Filesystem.writeFile({
            path: fileName,
            data: base64,
            directory: Directory.Cache,
          });
        } else {
          await Filesystem.appendFile({
            path: fileName,
            data: base64,
            directory: Directory.Cache,
          });
        }
      }

      const { uri } = await Filesystem.getUri({
        path: fileName,
        directory: Directory.Cache,
      });
      inputPaths.push(uri);
    }

    const outputFileName = `tl_merge_output_${recording.id}_${timestamp}.mp4`;
    const { uri: outputUri } = await Filesystem.getUri({
      path: outputFileName,
      directory: Directory.Cache,
    });

    const mergeResult = await AudioMerge.mergeFiles({
      inputPaths,
      outputPath: outputUri,
    });

    if (!mergeResult.success) {
      tlLogger.error('AudioSync:MergeFailed:FallingBackToConcat', {
        recordingId: recording.id,
      });
      return new Blob(allBlobs, { type: mimeType });
    }

    const { uri: mergedFileUri } = await Filesystem.getUri({
      path: outputFileName,
      directory: Directory.Cache,
    });
    const nativeUrl = Capacitor.convertFileSrc(mergedFileUri);
    const mergedBlob = await (await fetch(nativeUrl)).blob();

    return mergedBlob;
  } catch (err) {
    tlLogger.error('AudioSync:MergeError:FallingBackToConcat', {
      recordingId: recording.id,
      error: err instanceof Error ? err.message : String(err),
    });
    return new Blob(allBlobs, { type: mimeType });
  } finally {
    for (const fileName of inputFileNames) {
      try {
        await Filesystem.deleteFile({
          path: fileName,
          directory: Directory.Cache,
        });
      } catch {
        // non-fatal
      }
    }
  }
}
